import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Typography } from '@mui/material';

import {
  CasBaseDialog,
  CasButton,
  CasChip,
  CasConfirmationDialog,
  CasRenderDate,
  CasTable,
} from 'src/compass/shared/components';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import ICasRenderCellProps from 'src/compass/shared/components/CasTable/ICasRenderCellProps';
import { PENDING_EMAILS_QUERY_KEY } from 'src/compass/shared/components/CasPendingEmails/usePendingEmails';
import useAddMutation from 'src/compass/shared/hooks/reactQuery/useAddMutation';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';
import { onSuccessEnqueueSnackbar } from 'src/compass/shared/util/onSuccessEnqueueSnackbar';

interface Props {
  sentToday: boolean;
  onClose: VoidFunction;
}

// One row per reminder email a Send would queue. The list is recomputed on the server at Send, so
// what is shown here is a preview, not the payload.
export default function ClaimDueRemindersDialog({ sentToday, onClose }: Props) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isFetching } = useRetrieve({ url: 'Sce/Claims/DueReminders/List' });

  const rows: any[] = data ?? [];
  const sendableCount = rows.filter((row) => !row.unsendableReason).length;

  const { mutate } = useAddMutation({
    url: 'Sce/Claims/DueReminders/Send',
    onSuccess: (response: any) => {
      onSuccessEnqueueSnackbar(response);
      queryClient.invalidateQueries({ queryKey: [PENDING_EMAILS_QUERY_KEY] });
      onClose();
    },
  });

  const columns = [
    { field: 'claimNumber', label: 'Claim#' },
    { field: 'insuredName' },
    { field: 'dueDate', label: 'Due', render: CasRenderDate },
    { field: 'daysLeft' },
    {
      field: 'to',
      render: ({ row }: ICasRenderCellProps) =>
        row.unsendableReason ? (
          <CasChip color="warning" label={row.unsendableReason} />
        ) : (
          row.to.map((address: string) => <div key={address}>{address}</div>)
        ),
    },
    // Without a floor the table squeezes Subject to ~100px, the text wraps onto 7 lines, and every
    // row grows taller than its Body - leaving a band of white space under the endorsement table.
    { field: 'subject', minWidth: 300 },
    {
      field: 'body',
      render: ({ row }: ICasRenderCellProps) => (
        // eslint-disable-next-line react/no-danger
        <Box dangerouslySetInnerHTML={{ __html: row.body }} />
      ),
    },
  ];

  return (
    <CasBaseDialog
      open
      fullScreen
      showCloseIcon
      title="Claim Due Reminders"
      onClose={onClose}
      actions={
        <CasButton
          disabled={isFetching || sentToday || sendableCount === 0}
          onClick={() => setConfirmOpen(true)}
        >
          Send {sendableCount} Messages
        </CasButton>
      }
    >
      {sentToday && (
        <Typography
          variant="body2"
          sx={{
            display: 'inline-block',
            mb: 1,
            px: 0.75,
            py: 0.25,
            borderRadius: 0.5,
            // Same highlight as the dashboard's "Not sent today".
            bgcolor: 'warning.lighter',
          }}
        >
          Claim due reminders have already been sent today.
        </Typography>
      )}

      {isFetching ? (
        <CasLoader />
      ) : (
        <CasTable
          data={{ result: rows }}
          columns={columns}
          showPagination={false}
          columnsSortable={false}
          // CasTable caps its own scroll area at 60vh (Table.scss), which leaves the bottom of a
          // full-screen dialog empty. Let the table grow and the dialog content scroll instead.
          enableVerticalScroll={false}
        />
      )}

      {confirmOpen && (
        <CasConfirmationDialog
          open
          title="Send claim due reminders"
          confirmLabel={`Send ${sendableCount} Messages`}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false);
            mutate({});
          }}
        >
          Send {sendableCount} claim due reminder email(s) from lrp@compassagsolutions.com?
        </CasConfirmationDialog>
      )}
    </CasBaseDialog>
  );
}
